import { Component, OnDestroy } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { HeaderComponent } from './components/header/header.component';

import { ModulesService } from './services/modules.service';
import { filter } from 'rxjs';
import { BackgroundService } from './services/background.service';
import { SetBackgroundDirective } from './directives/set-background.directive';
import { AuthService } from './services/auth.service';
import { Chatbotv2Component } from './components/chatbotv2/chatbotv2.component';
import { TranslationService } from './services/translation.service';
import { BackgroundOverlayComponent } from './components/background-overlay/background-overlay.component';
import { AnimationOptions, LottieComponent } from 'ngx-lottie';
import { AnimationItem } from 'lottie-web';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, HeaderComponent, SetBackgroundDirective, Chatbotv2Component, BackgroundOverlayComponent, LottieComponent],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnDestroy {
  title = 'BeDiCi';
  showHeader = false;
  showChatbotButton = false;
  showChatbot = false;
  options: AnimationOptions = {
    path: '/assets/lottie/dici/robot_parle.json',
    loop: false,
    autoplay: true,
  };
  private animationItem!: AnimationItem;
  private animationFiles = ['/assets/lottie/dici/robot_parle.json', '/assets/lottie/dici/robot_surprise.json', '/assets/lottie/dici/robot_reflexion.json', '/assets/lottie/dici/robot_doute.json'];
  private currentAnimationIndex = 0;
  private animationTimeout?: number;

  constructor(
    private router: Router,
    private moduleService: ModulesService,
    private backgroundService: BackgroundService,
    private authService: AuthService,
    private translationService: TranslationService,
  ) {
    // Initialize translation service on app startup
    // This will trigger language detection and preference loading
  }

  ngOnInit(): void {
    this.showChatbotButton = this.authService.isLogged();

    this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((event) => {
      const shouldShow =
        event.url !== '/login' &&
        !event.url.includes('/onboarding') &&
        event.url !== '/signup' &&
        event.url !== '/datapolicy' &&
        event.url !== '/forgot-password' &&
        !event.url.includes('/verify') &&
        !event.url.includes('/reset-password');
      this.showHeader = shouldShow;

      const backgroundColor = this.determineBackgroundColor(event.url);
      this.backgroundService.updateBackgroundColor(backgroundColor);

      this.updateChatbotButtonVisibility(event.url);
    });
  }

  ngOnDestroy(): void {
    // Clean up timeout when component is destroyed
    if (this.animationTimeout) {
      clearTimeout(this.animationTimeout);
    }
  }

  updateChatbotButtonVisibility(url: string): void {
    this.showChatbotButton = this.authService.isLogged();
  }

  determineBackgroundColor(url: string): string {
    if (
      url === '/' ||
      url === '/login' ||
      url === '/signup' ||
      url === '/forgot-password' ||
      url.includes('/verify') ||
      url.includes('/reset-password') ||
      url.includes('bridge') ||
      url === '/parcourstp' ||
      url === '/survey' ||
      url.includes('/onboarding') ||
      url.includes('/payment') ||
      url.includes('/offers')
    ) {
      // Group 1: Authentication and general pages
      return '#0f102e';
    } else if (
      // Group 2: User dashboard and settings
      url === '/settings' ||
      url === '/follow_up' ||
      url === '/follow_up_parent' ||
      url === '/teaching_sheets' ||
      url === '/helptp' ||
      url === '/help' ||
      url === '/homec' ||
      url === '/hometp' ||
      url === '/update_avatar'
    ) {
      return '#0f102e';
    } else if (url.includes('/module_start/') || url.includes('/module_end/')) {
      // Group 3: Module pages (child)
      let array = url.split('/');
      let moduleId = array[array.length - 1];

      return this.moduleService.getModuleColorName(moduleId, undefined);
    } else if (url.includes('/module_take/')) {
      // Group 4: Module take pages (child)
      let array = url.split('/');
      let moduleId = array[array.length - 1];

      return this.moduleService.getModuleColorName(moduleId, undefined);
    } else if (url.includes('/module_start_tp') || url.includes('/module_end_tp')) {
      // Group 5: Module pages (teacher/parent)
      let array = url.split('/');
      let color = array[array.length - 2];

      return this.moduleService.getMetaColorFromBgColor(color);
    } else if (url.includes('/module_take_tp')) {
      // Group 6: Module take pages (teacher/parent)
      let array = url.split('/');
      let color = array[array.length - 2];

      return this.moduleService.getMetaColorFromBgColor(color);
    }

    return '#0f102e';
  }

  toggleChatbot(): void {
    this.showChatbot = !this.showChatbot;
  }

  animationCreated(animationItem: AnimationItem): void {
    this.animationItem = animationItem;
    this.animationItem.addEventListener('complete', this.onComplete.bind(this));
  }

  onComplete(): void {
    // Clear any existing timeout
    if (this.animationTimeout) {
      clearTimeout(this.animationTimeout);
    }

    // Wait 2 seconds before switching to next animation
    this.animationTimeout = window.setTimeout(() => {
      this.switchToNextAnimation();
    }, 2000);
  }

  private switchToNextAnimation(): void {
    // Move to next animation in the cycle
    this.currentAnimationIndex = (this.currentAnimationIndex + 1) % this.animationFiles.length;

    // Update the animation path
    this.options = {
      ...this.options,
      path: this.animationFiles[this.currentAnimationIndex],
    };
  }
}
